/// <amd-module name="Mention/Autocomplete/Components/AutocompleteComponent" />
import * as Resources from "Mention/Autocomplete/Resources";
import { getAutocompleteProviders, getProviderForShortcutCharacter } from "Mention/AutocompleteResolver/AutocompleteContributionResolverService";
import * as React from "react";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Callout, ContentSize } from "VSSUI/Callout";
import { ISuggestionItemProps, SuggestionsList } from "VSSUI/SuggestionsList";
import { getSafeId } from "VSSUI/Util";
import { Location } from "VSSUI/Utilities/Position";
import { createAutocompleteInputProxy, IAutocompleteInputProxy, isAutoCompleteInputProxy } from "../AutocompleteInputProxy";
import { IAutocompleteComponent, IAutocompleteProvider, IAutocompleteSuggestedItem, ISuggestionSearchResultItem } from "../Contracts";
import { AutocompleteFooter } from "./AutocompleteFooter";

const ARIA_ATTRIBUTES = {
    ActiveDescendant: "aria-activedescendant",
    Autocomplete: "aria-autocomplete",
    Label: "aria-label",
    Controls: "aria-controls"
};

const SEARCH_DELAY = 200;
const CALLOUT_ID = "autocomplete-callout";
const WHITESPACE_REGEX = /\s/;

export interface IAutocompletePluginOptions {
    onSelected?: (item: IAutocompleteSuggestedItem) => void;
}

interface IAutocompleteComponentProps {
    inputElement?: React.RefObject<HTMLTextAreaElement>;
    onInputChange?: (newContent: string) => void;
    isSuggesting?: ObservableValue<boolean>;
    insertAsHtml?: boolean;
}

export interface IAutocompleteComponentState {
    statusText: string;
    searchTerm: string;
    isSearchEnabled: boolean;
    isVisible: boolean;
    onDismiss: ((event?: Event | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => boolean) | undefined;
    onSuggestionClick?: () => void;
}

/**
 * Manages mention autocomplete providers, handles key events and opens/closes callout
 */
export class AutocompleteComponent extends VssComponent<IAutocompleteComponentProps, IAutocompleteComponentState> implements IAutocompleteComponent {
    protected _inputProxy?: IAutocompleteInputProxy;
    private _timer?: number;
    private _eventHandledOnKeyDown: boolean;
    private _isSuggesting: ObservableValue<boolean>;
    private _suggestionInfo: { startCharacter: string; startIndex: number };
    private _selectedObservable: ObservableValue<string | null> | null;
    private _pasting = true;

    // suggestions
    private _suggestions: ObservableArray<ISuggestionSearchResultItem> = new ObservableArray<ISuggestionSearchResultItem>();
    private _selectedItem?: ISuggestionSearchResultItem;
    private _currentIndex: ObservableValue<number> = new ObservableValue<number>(0);
    private _updateIndexTimer: number | undefined;

    // autocomplete providers
    private _currentProvider: IAutocompleteProvider | undefined;

    private _lastKeyWasModifier: boolean;
    private _anchorOrigin = { horizontal: Location.start, vertical: Location.end };
    private _calloutOrigin = { horizontal: Location.start, vertical: Location.start };

    constructor(props: IAutocompleteComponentProps, context: IVssComponentContext) {
        super(props, context);

        this._isSuggesting = props.isSuggesting || new ObservableValue<boolean>(false);

        // ensure that we've started fetching auto-complete providers
        getAutocompleteProviders(context.pageContext);

        this.state = {
            isVisible: false,
            onDismiss: undefined,
            statusText: this._getStatusText(this._suggestions.value.length)
        } as IAutocompleteComponentState;
    }

    public bind(inputElement: HTMLTextAreaElement | HTMLDivElement | IAutocompleteInputProxy): void {
        this._inputProxy = isAutoCompleteInputProxy(inputElement)
            ? inputElement
            : createAutocompleteInputProxy(inputElement, this.props.onInputChange);
        this._inputProxy.addKeyDownListener(this._onKeyDown);
        this._inputProxy.addKeyUpListener(this._onKeyUp);
        this._inputProxy.addBlurListener(this._onBlur);
        this._inputProxy.addInputListener(this._onInput);
        this._inputProxy.addPasteListener(this._onPaste);
    }

    public dispose(): void {
        if (this._inputProxy) {
            this.unbind();
            this._inputProxy = undefined;
        }
    }

    public componentDidMount(): void {
        if (this.props.inputElement && this.props.inputElement.current) {
            this.bind(this.props.inputElement.current);
        }
    }

    public componentWillUnmount(): void {
        if (this._selectedObservable) {
            this._selectedObservable.unsubscribe(this._handleSelectedLoaded);
        }
    }

    public render(): JSX.Element | null {
        const { isVisible } = this.state;
        if (!isVisible) {
            return null;
        }

        return (
            <Callout
                id={CALLOUT_ID}
                className="mention-autocomplete-component-callout"
                anchorOrigin={this._anchorOrigin}
                anchorElement={this._inputProxy?.getElementForAnchor()}
                onDismiss={this._onCalloutDismiss}
                calloutOrigin={this._calloutOrigin}
                contentShadow={true}
                contentSize={ContentSize.Auto}
            >
                <SuggestionsList<ISuggestionSearchResultItem>
                    className="autocomplete-suggestion-list"
                    loadingText={Resources.Loading}
                    noResultsFoundText={Resources.NoResultFound}
                    onSuggestionClicked={this._onClick}
                    renderSuggestion={this._onRenderSuggestion}
                    selectedIndex={this._currentIndex}
                    suggestions={this._suggestions}
                    width={this._inputProxy?.getClientWidth()}
                />
                <AutocompleteFooter statusText={this.state.statusText} />
            </Callout>
        );
    }

    private _getStatusText(suggestionCount: number): string {
        return suggestionCount > 0 ? Utils_String.format(Resources.StatusTextTemplate, suggestionCount) : "";
    }

    private _onClick = (suggestion: ISuggestionItemProps<ISuggestionSearchResultItem>): void => {
        this._selectedItem = suggestion.item;
        const { onSuggestionClick } = this.state;

        onSuggestionClick && onSuggestionClick();
    };

    private _onBlur = (): void => {
        this._setIsSuggesting(false);
    };

    private _onPaste = (event: ClipboardEvent): void => {
        if (this._isSuggesting.value && event.clipboardData && this._inputProxy) {
            event.preventDefault();
            let pastedText = event.clipboardData.getData("text/plain").trim();
            const { value, selectionEnd } = this._inputProxy.getSelection();
            const { startIndex, startCharacter } = this._suggestionInfo;
            let wholeText = selectionEnd <= startIndex ? "" : value.substring(startIndex, selectionEnd);
            wholeText += pastedText;
            document.execCommand("insertText", false, pastedText);
            this._updateSuggestions(wholeText, startCharacter);
        }

        this._pasting = true;
    };

    private _openCallout(
        onSuggestionClick: () => void,
        onDismiss?: (event?: Event | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => boolean
    ): void {
        if (!this._currentProvider) {
            return;
        }

        const callback = (items: ISuggestionSearchResultItem[]) => {
            // Check if we are still suggesting after provider gives results
            if (!this._isSuggesting.value) {
                return;
            }

            this._suggestions.value = items;

            const statusText = this._getStatusText(items.length);

            this.setState({
                isVisible: true,
                onSuggestionClick,
                onDismiss,
                statusText,
                isSearchEnabled: false
            });
        };

        const { value, startIndex } = this._getWordAtCursor();
        if (value.length > 1) {
            this._currentProvider.search(value.substr(1), false).then(callback);
        } else {
            this._currentProvider.getSuggestions().then(callback);
        }
    }

    private _onCalloutDismiss = (ev?: Event | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void => {
        const handled = this.state.onDismiss && this.state.onDismiss(ev);
        if (handled) {
            return;
        }

        this.closeCallout();
    };

    public closeCallout(): void {
        this._currentIndex.value = 0;
        this.setState({ isVisible: false });
    }

    private unbind(): void {
        this._inputProxy?.removeListeners();
    }

    private _onInput = (): void => {
        const { value, startIndex } = this._getWordAtCursor();
        if (!this._isSuggesting.value && value) {
            this._onValueChange(value.substr(0, 1));
            return;
        }

        const isSuggestionCharacterRemoved = !value && startIndex === this._suggestionInfo?.startIndex;
        if (this._isSuggesting.value && isSuggestionCharacterRemoved) {
            this._setIsSuggesting(false);
        }
    };

    private _isModifierKey(key: string): boolean {
        return ["Shift", "Control", "Alt", "Command"].indexOf(key) > -1;
    }

    private _onKeyDown = (ev: KeyboardEvent): void => {
        this._eventHandledOnKeyDown = false;
        if (this._isSuggesting.value) {
            this._onKeyDownForSuggestions(ev);
        }
        if (this._currentProvider?.onKeyDown) {
            this._currentProvider.onKeyDown!(ev, this._inputProxy!.getElementForAnchor(), this._selectedItem);
        }
    };

    private _onKeyUp = (ev: KeyboardEvent): void => {
        if (this._isModifierKey(ev.key)) {
            this._lastKeyWasModifier = true;
            return;
        }

        if (this._eventHandledOnKeyDown) {
            this._lastKeyWasModifier = false;
            return;
        }

        if (this._isSuggesting.value) {
            this._onKeyUpForSuggestions(ev);
            this._lastKeyWasModifier = false;
            return;
        }

        this._onValueChange(ev.key);

        this._lastKeyWasModifier = false;
    };

    private _onValueChange = (key: string): void => {
        const { value, startIndex } = this._getWordAtCursor();
        this._currentProvider = getProviderForShortcutCharacter(this.context.pageContext, key);
        if (!this._currentProvider && this._lastKeyWasModifier) {
            key = value.substr(0, 1);
            this._currentProvider = getProviderForShortcutCharacter(this.context.pageContext, key);
        }

        if (this._currentProvider && value.substr(0, 1) === key) {
            this._suggestionInfo = { startIndex, startCharacter: key };
            this._setIsSuggesting(true);
        }
    };

    private _onKeyUpForSuggestions(ev: KeyboardEvent): void {
        if (!this._inputProxy || this._pasting) {
            this._pasting = false;
            return;
        }

        // if this is a character key or backspace, clear the timer as we will either queue a new timer or stop suggesting
        if ((ev.key.length === 1 && ev.key !== " ") || ev.key === "Backspace") {
            window.clearTimeout(this._timer);
            this._timer = undefined;
        }

        const { value, selectionEnd } = this._inputProxy.getSelection();
        const { startIndex, startCharacter } = this._suggestionInfo;
        const wholeText = selectionEnd <= startIndex ? "" : value.substring(startIndex, selectionEnd);
        this._updateSuggestions(wholeText, startCharacter);
    }

    private _updateSuggestions = (wholeText: string, startCharacter: string) => {
        if (wholeText.substr(0, 1) === startCharacter) {
            this._timer = window.setTimeout(() => {
                if (this._currentProvider) {
                    const term = wholeText.substr(1);
                    this._currentProvider.search(term, false).then((result: ISuggestionSearchResultItem[]) => {
                        const statusText = this._getStatusText(result.length);

                        // tODO: search
                        // result = isSearchEnabled ? result : [...result, SEARCH_BUTTON_SUGGESTION];
                        this._suggestions.value = result;

                        if (this._updateIndexTimer) {
                            window.cancelAnimationFrame(this._updateIndexTimer);
                        }

                        // Selected index set after list is updated for screen readers.
                        this._updateIndexTimer = window.requestAnimationFrame(() => {
                            this._currentIndex.value = 0;
                            if (this._suggestions.length) {
                                this._selectedItem = this._suggestions.value[this._currentIndex.value];
                            } else {
                                this._selectedItem = undefined;
                            }
                            this.setState({ searchTerm: term, statusText });
                        });
                    });

                    this._timer = undefined;
                }
            }, SEARCH_DELAY);
        } else {
            this._setIsSuggesting(false);
        }
    };

    private _onKeyDownForSuggestions(ev: KeyboardEvent): void {
        if (!this._inputProxy) {
            return;
        }

        switch (ev.key) {
            case "Enter":
            case "Tab":
                this._handleSelect();
                this._handleEventOnKeyDown(ev);
                break;
            case "Down":
            case "Up":
            case "ArrowUp":
            case "ArrowDown":
                if (!this._currentProvider) {
                    break;
                }

                const nextIndex = ev.key.substr(-2) === "Up" ? this.previousSuggestion() : this.nextSuggestion();
                if (nextIndex >= 0) {
                    this._inputProxy.setInputAttribute(ARIA_ATTRIBUTES.ActiveDescendant, this._getElementIdByIndex(nextIndex));
                }
                this._handleEventOnKeyDown(ev);
                break;
            case "Escape":
                this._setIsSuggesting(false);
                this._handleEventOnKeyDown(ev);
        }
    }

    public getId(): string {
        return getSafeId("suggestion-list") || "";
    }

    public nextSuggestion(): number {
        const suggestionsLength = this._suggestions.value.length;
        let newIndex: number = this._currentIndex.value;
        newIndex++;
        if (newIndex >= suggestionsLength) {
            newIndex = 0;
        }
        this._selectedItem = this._suggestions.value[newIndex];
        this._currentIndex.value = newIndex;
        this.forceUpdate();
        return this._currentIndex.value;
    }

    public previousSuggestion(): number {
        const suggestionsLength = this._suggestions.value.length;
        let newIndex: number = this._currentIndex.value;
        newIndex--;
        if (newIndex < 0) {
            newIndex = suggestionsLength - 1;
        }
        this._selectedItem = this._suggestions.value[newIndex];
        this._currentIndex.value = newIndex;

        this.forceUpdate();
        return this._currentIndex.value;
    }

    public getSelectedItem(): IAutocompleteSuggestedItem | null | undefined {
        if (!this._currentProvider) {
            return null;
        }

        if (!this._selectedItem && this._suggestions.length) {
            this._selectedItem = this._suggestions.value[this._currentIndex.value];
        }

        // tODO: search
        // let selectedItem: ISearchResultWorkItem = this._selectedItem;

        // if (selectedItem === SEARCH_BUTTON_SUGGESTION) {
        //     this._enableSearchAndCall();
        //     return null;
        // }

        if (this._selectedItem) {
            const value = this.props.insertAsHtml
                ? this._currentProvider.getReplacementHtml(this._selectedItem)
                : this._currentProvider.getReplacementText(this._selectedItem);
            this._selectedItem = undefined;
            return { value };
        }
    }

    private _onRenderSuggestion = (suggestionItemProps: ISuggestionItemProps<ISuggestionSearchResultItem>): JSX.Element => {
        let row: JSX.Element | null = null;
        if (this._currentProvider) {
            row = this._currentProvider.renderSuggestion(suggestionItemProps.item, this._inputProxy?.getElementForAnchor());
        }

        return <div className="mention-suggestions-row flex-grow">{row}</div>;
    };

    private _onDismiss = (ev?: Event | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): boolean => {
        if (ev && ev.type === "focus" && this._inputProxy?.isTargetedByEvent(ev)) {
            ev.preventDefault();
            return true;
        }

        this._setIsSuggesting(false);
        return false;
    };

    private _handleSelect = (): void => {
        if (this._currentProvider && this._currentProvider.onSelect && this._selectedItem) {
            const selected = this._currentProvider.onSelect(this._selectedItem, this.props.insertAsHtml);
            if (selected.value) {
                this._updateSelection({ value: selected.value });
            } else {
                if (this._selectedObservable) {
                    this._selectedObservable.unsubscribe(this._handleSelectedLoaded);
                }
                this._selectedObservable = selected;
                selected.subscribe(this._handleSelectedLoaded);
            }
        } else {
            this._updateSelection(null);
        }
    };

    private _handleSelectedLoaded = (value: string | null): void => {
        if (this._selectedObservable) {
            this._selectedObservable.unsubscribe(this._handleSelectedLoaded);
            this._selectedObservable = null;
        }
        if (value) {
            this._updateSelection({ value });
        }
    };

    private _updateSelection = (selected?: IAutocompleteSuggestedItem | null): void => {
        if (!this._inputProxy) {
            return;
        }

        if (selected === null) {
            selected = this._currentProvider ? this.getSelectedItem() : null;
        }
        if (selected === null) {
            // null selected data means controller has already handled the selection
            // but we still need to focus back into the input element in case controller
            this._inputProxy?.focus();
            return;
        }
        if (selected === undefined) {
            // undefined selected data means no result
            this._setIsSuggesting(false);
            return;
        }

        this._inputProxy?.focus();
        const result = `${selected.value} `;

        this._setIsSuggesting(false);
        const insertText = () => {
            if (this._inputProxy) {
                this._inputProxy.insertText(result, this._suggestionInfo.startIndex);
            }
        };

        requestAnimationFrame(insertText);
    };

    private _handleEventOnKeyDown = (event: KeyboardEvent): void => {
        this._eventHandledOnKeyDown = true;
        event.preventDefault();
        event.stopImmediatePropagation();
    };

    private _setIsSuggesting = (isSuggesting: boolean): void => {
        if (this._isSuggesting.value === isSuggesting) {
            return;
        }

        this._isSuggesting.value = isSuggesting;
        if (this._isSuggesting.value) {
            const { _inputProxy: _autocompleteInputElement, _currentProvider: _currentMentionProvider } = this;
            if (!_autocompleteInputElement || !_currentMentionProvider) {
                return;
            }

            this._openCallout(this._handleSelect, this._onDismiss);

            // we need to delay so NVDA will announce the first selection
            setTimeout(() => {
                this._inputProxy?.setInputAttribute(ARIA_ATTRIBUTES.Autocomplete, "list");
                this._inputProxy?.setInputAttribute(ARIA_ATTRIBUTES.Controls, getSafeId(CALLOUT_ID) ?? "");
                this._inputProxy?.setInputAttribute(ARIA_ATTRIBUTES.Label, Resources.AutocompleteAriaLabel);
                const firstSelectionId = this._getElementIdByIndex(0);
                if (firstSelectionId) {
                    this._inputProxy?.setInputAttribute(ARIA_ATTRIBUTES.ActiveDescendant, firstSelectionId);
                }
            }, 0);
        } else {
            this._removeAutocompleteAriaAttributes();

            this.closeCallout();
            if (this._currentProvider) {
                this._currentProvider = undefined;
            }
        }
    };

    private _getElementIdByIndex(index: number): string {
        return getSafeId("sug-row-" + index) ?? "";
    }

    /**
     * Returns the word (defined as a sequence of non-whitespace characters) on which the cursor is located.
     *
     * (let | be the cursor)
     * `#|`          => `#`
     * `#1234|`      => `#1234`
     * `#12|34`      => `#1234`
     * `@Spoc|k`     => `@Spock`
     * `  #|  `      => `#`
     * `  #1234|  `  => `#1234`
     * `  #12|34  `  => `#1234`
     * `  @Spoc|k  ` => `@Spock`
     */
    private _getWordAtCursor(): { value: string; startIndex: number } {
        if (!this._inputProxy) {
            throw Error("input element is null");
        }

        const { selectionStart, selectionEnd, value } = this._inputProxy.getSelection();

        let startIndex = selectionStart;
        let endIndex = selectionEnd + 1; // Add 1 because `substring` will not include the end index

        // Move the start index to the left as long as there are non-whitespace characters to the left
        while (startIndex > 0 && !WHITESPACE_REGEX.test(value[startIndex - 1])) {
            --startIndex;
        }

        // Move the end index to the right as long as there are non-whitespace characters to the right
        while (endIndex < value.length && !WHITESPACE_REGEX.test(value[endIndex])) {
            ++endIndex;
        }

        const wordAtCursor = value.substring(startIndex, endIndex);

        return { value: wordAtCursor, startIndex };
    }

    private _removeAutocompleteAriaAttributes(): void {
        if (!this._inputProxy?.getInputAttribute(ARIA_ATTRIBUTES.Label)) {
            this._inputProxy?.setInputAttribute(ARIA_ATTRIBUTES.Label, Resources.AutocompleteAriaLabel);
        }
        this._inputProxy?.removeInputAttribute(ARIA_ATTRIBUTES.Autocomplete);
        this._inputProxy?.removeInputAttribute(ARIA_ATTRIBUTES.Controls);
        this._inputProxy?.removeInputAttribute(ARIA_ATTRIBUTES.ActiveDescendant);
    }
}

VssComponent.register("autocomplete-component", AutocompleteComponent);
